import React, { useState, useEffect } from 'react';
import { Plus, Save, AlertCircle, CheckCircle2, Star, ThumbsUp, Heart, Brain, Zap, BarChart3, Target, Eye, Settings, Palette } from 'lucide-react';
import { useStore } from '../store';
import { CustomScaleManager } from './CustomScaleManager';
import { MultiScaleProcessor, ScaleDefinition } from '../utils/multiScaleAnalysis';

// أنظمة ليكرت المحددة مسبقاً مع دعم متعدد المقاييس
const LIKERT_SCALES = {
  agreement: {
    name: 'نظام ليكرت - الموافقة',
    icon: ThumbsUp,
    description: 'مقياس الموافقة التقليدي من موافق بشدة إلى غير موافق بشدة',
    scales: {
      'موافق بشدة': 5,
      'موافق': 4,
      'محايد': 3,
      'غير موافق': 2,
      'غير موافق بشدة': 1
    }
  },
  satisfaction: {
    name: 'نظام ليكرت - الرضا',
    icon: Heart,
    description: 'مقياس الرضا من راضِ بشدة إلى غير راض بشدة',
    scales: {
      'راضِ بشدة': 5,
      'راض': 4,
      'محايد': 3,
      'غير راض': 2,
      'غير راض بشدة': 1
    }
  },
  quality: {
    name: 'نظام ليكرت - الجودة',
    icon: Star,
    description: 'مقياس الجودة من ممتاز إلى ضعيف جدا',
    scales: {
      'ممتاز': 5,
      'جيد جداً': 4,
      'جيد': 3,
      'محايد': 2,
      'ضعيف': 1,
      'ضعيف جداً': 1
    }
  },
  performance: {
    name: 'نظام ليكرت - الأداء',
    icon: Zap,
    description: 'مقياس الأداء من متميز إلى ضعيف جدا',
    scales: {
      'متميز': 5,
      'ممتاز': 5,
      'جيد جداً': 4,
      'جيد': 3,
      'ضعيف': 2,
      'ضعيف جداً': 1
    }
  },
  effectiveness: {
    name: 'نظام ليكرت - الفعالية',
    icon: Target,
    description: 'مقياس الفعالية للتدريب والأنشطة',
    scales: {
      'فعال جداً': 5,
      'فعال': 4,
      'متوسط الفعالية': 3,
      'قليل الفعالية': 2,
      'غير فعال': 1
    }
  },
  usability: {
    name: 'نظام ليكرت - سهولة الاستخدام',
    icon: Brain,
    description: 'مقياس سهولة الاستخدام من سهل جداً إلى ليس سهلاً على الإطلاق',
    scales: {
      'سهل جداً للاستخدام': 5,
      'سهل للاستخدام': 4,
      'محايد': 3,
      'غير سهل للاستخدام': 2,
      'ليس سهلاً للاستخدام على الإطلاق': 1
    }
  }
};

// دالة الكشف الذكي عن المقاييس
const detectScaleType = (uniqueValues: string[]): string | null => {
  const normalizedValues = uniqueValues.map(v => v.toLowerCase().trim());
  
  // كشف مقياس الموافقة
  const agreementKeywords = ['موافق', 'غير موافق', 'محايد'];
  if (agreementKeywords.some(keyword => 
    normalizedValues.some(val => val.includes(keyword))
  )) {
    return 'agreement';
  }
  
  // كشف مقياس الرضا
  const satisfactionKeywords = ['راض', 'غير راض'];
  if (satisfactionKeywords.some(keyword => 
    normalizedValues.some(val => val.includes(keyword))
  )) {
    return 'satisfaction';
  }
  
  // كشف مقياس الجودة
  const qualityKeywords = ['ممتاز', 'جيد', 'ضعيف'];
  if (qualityKeywords.some(keyword => 
    normalizedValues.some(val => val.includes(keyword))
  )) {
    return 'quality';
  }
  
  // كشف مقياس الأداء
  const performanceKeywords = ['متميز', 'أداء'];
  if (performanceKeywords.some(keyword => 
    normalizedValues.some(val => val.includes(keyword))
  )) {
    return 'performance';
  }
  
  // كشف مقياس الفعالية
  const effectivenessKeywords = ['فعال', 'فعالية'];
  if (effectivenessKeywords.some(keyword => 
    normalizedValues.some(val => val.includes(keyword))
  )) {
    return 'effectiveness';
  }
  
  // كشف مقياس سهولة الاستخدام
  const usabilityKeywords = ['سهل', 'سهولة', 'استخدام', 'للاستخدام'];
  if (usabilityKeywords.some(keyword => 
    normalizedValues.some(val => val.includes(keyword))
  )) {
    return 'usability';
  }
  
  return null;
};

// تطبيع النص العربي لإزالة التشكيل والعلامات الخاصة
const normalizeArabicText = (text: string): string => {
  return text
    .toLowerCase()
    .trim()
    // إزالة التشكيل
    .replace(/[\u064B-\u0652]/g, '')
    // تطبيع الألف
    .replace(/[آأإ]/g, 'ا')
    // تطبيع التاء المربوطة
    .replace(/ة/g, 'ه')
    // إزالة المسافات الإضافية
    .replace(/\s+/g, ' ');
};

// دالة التطابق الذكي للنصوص المحسّنة
const smartTextMatching = (inputText: string, scaleValues: Record<string, number>): number | null => {
  const normalizedInput = normalizeArabicText(inputText);
  
  // بحث دقيق أولاً مع التطبيع
  for (const [key, value] of Object.entries(scaleValues)) {
    const normalizedKey = normalizeArabicText(key);
    if (normalizedKey === normalizedInput) {
      return value;
    }
  }
  
  // بحث جزئي ذكي مع التطبيع
  for (const [key, value] of Object.entries(scaleValues)) {
    const normalizedKey = normalizeArabicText(key);
    if (normalizedInput.includes(normalizedKey) || normalizedKey.includes(normalizedInput)) {
      return value;
    }
  }
  
  // بحث بالكلمات الرئيسية المحسّن
  const keywordMaps = {
    'بشدة': ['شدة', 'قوي', 'كبير', 'جداً', 'جدا'],
    'محايد': ['متوسط', 'عادي', 'معتدل', 'متوازن'],
    'ضعيف': ['سيء', 'قليل', 'منخفض', 'رديء'],
    'ممتاز': ['رائع', 'عظيم', 'مدهش'],
    'موافق': ['أوافق', 'أؤيد', 'أقبل', 'مؤيد'],
    'راض': ['راضي', 'مرضي', 'سعيد', 'مسرور', 'راضِ'],
    'غير راض': ['غير راضي', 'غير مرضي', 'غير سعيد', 'مستاء'],
    'غير موافق': ['غير مؤيد', 'معارض', 'رافض'],
    'جيد': ['طيب', 'حسن', 'مقبول', 'لائق'],
    'فعال': ['مؤثر', 'نافع', 'مفيد', 'ناجح'],
    'سهل': ['يسير', 'بسيط', 'واضح', 'مفهوم']
  };
  
  for (const [key, value] of Object.entries(scaleValues)) {
    const normalizedKey = normalizeArabicText(key);
    for (const [keyword, synonyms] of Object.entries(keywordMaps)) {
      const normalizedKeyword = normalizeArabicText(keyword);
      if (normalizedKey.includes(normalizedKeyword)) {
        const normalizedSynonyms = synonyms.map(s => normalizeArabicText(s));
        if (normalizedSynonyms.some(synonym => normalizedInput.includes(synonym) || synonym.includes(normalizedInput))) {
          return value;
        }
      }
    }
  }
  
  return null;
};

export function ScaleConfig() {
  const { 
    scaleMapping, 
    setScaleMapping, 
    setCurrentPage, 
    canProceedToAnalysis, 
    fileData,
    axes 
  } = useStore();
  
  const [selectedScale, setSelectedScale] = useState<string | null>(null);
  const [showCustom, setShowCustom] = useState(false);
  const [newResponse, setNewResponse] = useState('');
  const [newValue, setNewValue] = useState<number>(3);
  const [error, setError] = useState<string | null>(null);
  const [detectedScales, setDetectedScales] = useState<Record<string, string>>({});
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [multiScaleMode, setMultiScaleMode] = useState(false);
  const [columnScaleMappings, setColumnScaleMappings] = useState<Record<string, Record<string, number>>>({});
  const [showCustomScaleManager, setShowCustomScaleManager] = useState(false);
  const [multiScaleProcessor] = useState(() => new MultiScaleProcessor());
  const [customScales, setCustomScales] = useState<Record<string, ScaleDefinition>>({});
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisMessage, setAnalysisMessage] = useState('');
  const [extractedResponses, setExtractedResponses] = useState<Record<string, number>>({});

  // تحميل المقاييس المخصصة
  useEffect(() => {
    setCustomScales(multiScaleProcessor.getCustomScales());
  }, [multiScaleProcessor]);

  // كشف المقاييس تلقائياً عند تحميل البيانات
  useEffect(() => {
    if (fileData && fileData.length > 0) {
      const detected: Record<string, string> = {};
      
      // جمع كل الأعمدة الموجودة في المحاور
      const allColumns = Object.values(axes).flat();
      
             allColumns.forEach(column => {
         const uniqueValues = [...new Set(
           fileData
             .map(row => row[column])
             .filter(val => val && typeof val === 'string' && val.trim() !== '')
             .map(val => val as string)
         )];
        
        if (uniqueValues.length > 1) {
          const detectedType = detectScaleType(uniqueValues);
          if (detectedType) {
            detected[column] = detectedType;
          }
        }
      });
      
      setDetectedScales(detected);
      
      // إذا تم اكتشاف مقاييس متعددة، فعل الوضع المتقدم
      const uniqueScaleTypes = [...new Set(Object.values(detected))];
      if (uniqueScaleTypes.length > 1) {
        setMultiScaleMode(true);
        setShowAdvancedOptions(true);
      }
    }
  }, [fileData, axes]);

  const handleSelectScale = (scaleType: keyof typeof LIKERT_SCALES) => {
    const scale = LIKERT_SCALES[scaleType];
    if (multiScaleMode) {
      // في الوضع متعدد المقاييس، طبق هذا المقياس على الأعمدة المناسبة
      const newColumnMappings = { ...columnScaleMappings };
      Object.entries(detectedScales).forEach(([column, detectedType]) => {
        if (detectedType === scaleType) {
          newColumnMappings[column] = scale.scales;
        }
      });
      setColumnScaleMappings(newColumnMappings);
    } else {
      // الوضع التقليدي
      setScaleMapping(scale.scales);
    }
    setSelectedScale(scaleType);
    setError(null);
  };

     // استخراج المقياس الفعلي من البيانات
   const extractActualScale = (column: string, scaleType: string): Record<string, number> => {
     if (!fileData) return {};
     
     const uniqueValues = [...new Set(
       fileData
         .map(row => row[column])
         .filter(val => val && typeof val === 'string' && val.trim() !== '')
         .map(val => val as string)
     )];

     // محاولة التطابق الذكي مع المقياس المكتشف
     const baseScale = LIKERT_SCALES[scaleType as keyof typeof LIKERT_SCALES]?.scales || {};
     const extractedScale: Record<string, number> = {};

           // تجربة التطابق المباشر أولاً مع التطبيع المحسّن
      uniqueValues.forEach(value => {
        const normalizedValue = normalizeArabicText(value);
        
        // البحث عن تطابق مباشر في المقياس الأساسي
        for (const [key, val] of Object.entries(baseScale)) {
          const normalizedKey = normalizeArabicText(key);
          if (normalizedKey === normalizedValue) {
            extractedScale[value] = val;
            return;
          }
        }
       
       // البحث عن تطابق جزئي ذكي
       const matchedValue = smartTextMatching(value, baseScale);
       if (matchedValue !== null) {
         extractedScale[value] = matchedValue;
       }
     });

     // إذا لم نجد تطابقات كافية، نستخدم التخمين الذكي
     if (Object.keys(extractedScale).length < uniqueValues.length * 0.6) {
       return createSmartMapping(uniqueValues, scaleType);
     }

     return extractedScale;
   };

   // إنشاء تخمين ذكي للمقياس
   const createSmartMapping = (values: string[], scaleType: string): Record<string, number> => {
     const mapping: Record<string, number> = {};
     const sortedValues = [...values].sort();
     
     // تحديد النطاق بناءً على نوع المقياس
     const ranges = {
       satisfaction: [1, 5],
       agreement: [1, 5], 
       quality: [1, 5],
       performance: [1, 5],
       effectiveness: [1, 5],
       usability: [1, 5]
     };
     
     const [minVal, maxVal] = ranges[scaleType as keyof typeof ranges] || [1, 5];
     const step = (maxVal - minVal) / (sortedValues.length - 1);
     
     sortedValues.forEach((value, index) => {
       mapping[value] = Math.round(maxVal - (index * step));
     });
     
     return mapping;
   };

   const handleAutoDetectAndApply = () => {
     if (!fileData || Object.keys(detectedScales).length === 0) {
       setError('لم يتم اكتشاف أي مقاييس في البيانات');
       return;
     }

     const newColumnMappings: Record<string, Record<string, number>> = {};
     let globalMapping: Record<string, number> = {};

     Object.entries(detectedScales).forEach(([column, scaleType]) => {
       // استخراج المقياس الفعلي من البيانات
       const actualScale = extractActualScale(column, scaleType);
       newColumnMappings[column] = actualScale;
       
       // دمج في المقياس العام
       globalMapping = { ...globalMapping, ...actualScale };
     });

     setColumnScaleMappings(newColumnMappings);
     setScaleMapping(globalMapping);
     setMultiScaleMode(true);
     setError(null);
   };

   const handleAddCustomMapping = () => {
     if (!newResponse.trim()) {
       setError('يرجى إدخال نص الاستجابة');
       return;
     }

     const updatedMapping = {
       ...scaleMapping,
       [newResponse.trim()]: newValue
     };
     setScaleMapping(updatedMapping);
     setNewResponse('');
     setNewValue(3);
     setError(null);
   };

  const handleUpdateValue = (response: string, value: number) => {
    const updatedMapping = {
      ...scaleMapping,
      [response]: value
    };
    setScaleMapping(updatedMapping);
  };

  const handleDeleteMapping = (response: string) => {
    const { [response]: _, ...updatedMapping } = scaleMapping;
    setScaleMapping(updatedMapping);
  };

  const handleProceed = () => {
    if (Object.keys(scaleMapping).length === 0) {
      setError('يجب اختيار مقياس أو إضافة مقياس واحد على الأقل');
      return;
    }
    setCurrentPage('analysis');
  };

  // دوال إدارة المقاييس المخصصة
  const handleAddCustomScale = (scaleId: string, definition: Omit<ScaleDefinition, 'type'>) => {
    multiScaleProcessor.addCustomScale(scaleId, definition);
    setCustomScales(multiScaleProcessor.getCustomScales());
  };

  const handleUpdateCustomScale = (scaleId: string, definition: Omit<ScaleDefinition, 'type'>) => {
    multiScaleProcessor.updateCustomScale(scaleId, definition);
    setCustomScales(multiScaleProcessor.getCustomScales());
  };

  const handleRemoveCustomScale = (scaleId: string) => {
    multiScaleProcessor.removeCustomScale(scaleId);
    setCustomScales(multiScaleProcessor.getCustomScales());
  };

  const isScaleComplete = Object.keys(scaleMapping).length > 0;
  const hasDetectedScales = Object.keys(detectedScales).length > 0;

  // دالة التحليل الذكي للبيانات
  const analyzeResponses = () => {
    if (!fileData || !workshopColumn) return;

    setIsAnalyzing(true);
    setAnalysisMessage('جاري تحليل البيانات...');

    setTimeout(() => {
      try {
        const allResponses: string[] = [];
        const headers = Object.keys(fileData[0] || {});
        const questionColumns = headers.filter(h => h !== workshopColumn);

        // استخراج جميع الاستجابات من أعمدة الأسئلة
        questionColumns.forEach(column => {
          fileData.forEach(row => {
            const response = row[column];
            if (response !== null && response !== undefined && response !== '') {
              // حفظ البيانات كما هي بدون تطبيع مدمر
              if (typeof response === 'number') {
                allResponses.push(response.toString());
              } else {
                allResponses.push(response.toString());
              }
            }
          });
        });

        // تحليل الاستجابات المطبعة
        const uniqueValues = [...new Set(allResponses)].sort();
        const sampleText = uniqueValues.join(' ');

        setAnalysisMessage(`تم العثور على ${uniqueValues.length} استجابات فريدة من ${allResponses.length} استجابة إجمالية.`);

        // استخراج المقياس
        const extractedScale = extractScaleFromText(sampleText, uniqueValues);
        
        if (Object.keys(extractedScale).length > 0) {
          // حفظ المقياس كما هو بدون تطبيع مدمر
          setExtractedResponses(extractedScale);
          setAnalysisMessage('تم استخراج المقياس بنجاح! يمكنك مراجعته وتعديله حسب الحاجة.');
        } else {
          setAnalysisMessage('لم يتم العثور على مقياس واضح. يرجى استخدام الأنظمة الجاهزة أو إنشاء مقياس مخصص.');
        }

      } catch (error) {
        console.error('Error during analysis:', error);
        setAnalysisMessage('حدث خطأ أثناء التحليل. يرجى المحاولة مرة أخرى.');
      } finally {
        setIsAnalyzing(false);
      }
    }, 1000);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" dir="rtl">
      <h2 className="text-2xl font-bold mb-6 text-right">إعداد مقياس الاستجابة المتقدم 🔢</h2>

      {/* معلومات الكشف التلقائي */}
      {hasDetectedScales && (
        <div className="bg-blue-50 rounded-lg shadow p-6 mb-6 border border-blue-200">
          <div className="flex items-center gap-3 mb-4">
            <Brain className="w-6 h-6 text-blue-600" />
            <h3 className="text-lg font-semibold text-blue-800">🧠 الكشف الذكي للمقاييس</h3>
          </div>
          
          <p className="text-blue-700 text-right mb-4">
            تم اكتشاف المقاييس التالية في بياناتك تلقائياً. عند الضغط على "تطبيق المقاييس المكتشفة تلقائياً" سيتم استخراج القيم الفعلية من ملفك:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
            {Object.entries(detectedScales).map(([column, scaleType]) => (
              <div key={column} className="bg-white p-3 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  {LIKERT_SCALES[scaleType as keyof typeof LIKERT_SCALES]?.icon && 
                    React.createElement(LIKERT_SCALES[scaleType as keyof typeof LIKERT_SCALES].icon, 
                      { className: "w-4 h-4 text-blue-600" }
                    )
                  }
                  <span className="font-medium text-sm text-blue-800">
                    {LIKERT_SCALES[scaleType as keyof typeof LIKERT_SCALES]?.name || scaleType}
                  </span>
                </div>
                <p className="text-xs text-gray-600 text-right">{column}</p>
              </div>
            ))}
          </div>

          <button
            onClick={handleAutoDetectAndApply}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto"
          >
            <Brain className="w-5 h-5" />
            <span>تطبيق المقاييس المكتشفة تلقائياً</span>
          </button>
        </div>
      )}

      {/* إدارة المقاييس المخصصة */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-right">المقاييس المخصصة</h3>
          <button
            onClick={() => setShowCustomScaleManager(!showCustomScaleManager)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Palette className="w-4 h-4" />
            {showCustomScaleManager ? 'إخفاء إدارة المقاييس' : 'إدارة المقاييس المخصصة'}
          </button>
        </div>
        
        {showCustomScaleManager && (
          <CustomScaleManager
            customScales={customScales}
            onAddScale={handleAddCustomScale}
            onUpdateScale={handleUpdateCustomScale}
            onRemoveScale={handleRemoveCustomScale}
          />
        )}
      </div>

      {/* أنظمة ليكرت الجاهزة */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-right">اختر نظام ليكرت</h3>
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className="text-blue-500 hover:text-blue-600 flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            <span>{showAdvancedOptions ? 'إخفاء' : 'إظهار'} الخيارات المتقدمة</span>
          </button>
        </div>
        
        <p className="text-gray-600 text-right mb-6">اختر أحد الأنظمة الجاهزة أو أضف مقياسك المخصص</p>
        
        {showAdvancedOptions && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <label className="flex items-center gap-3 text-right">
              <input
                type="checkbox"
                checked={multiScaleMode}
                onChange={(e) => setMultiScaleMode(e.target.checked)}
                className="rounded"
              />
              <span className="font-medium">وضع المقاييس المتعددة</span>
              <BarChart3 className="w-5 h-5 text-gray-600" />
            </label>
            <p className="text-sm text-gray-600 text-right mt-2">
              فعل هذا الخيار للتعامل مع مقاييس مختلفة في أعمدة مختلفة من نفس الاستبانة
            </p>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {/* المقاييس الافتراضية */}
          {Object.entries(LIKERT_SCALES).map(([key, scale]) => (
            <div
              key={key}
              className={`
                border-2 rounded-lg p-4 cursor-pointer transition-all
                ${selectedScale === key 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'}
              `}
              onClick={() => handleSelectScale(key as keyof typeof LIKERT_SCALES)}
            >
              <div className="flex items-center gap-3 mb-3">
                <scale.icon className={`w-6 h-6 ${selectedScale === key ? 'text-blue-500' : 'text-gray-500'}`} />
                <h4 className="font-semibold text-right flex-1 text-sm">{scale.name}</h4>
                {selectedScale === key && <CheckCircle2 className="w-5 h-5 text-blue-500" />}
              </div>
              <p className="text-xs text-gray-600 text-right mb-3">{scale.description}</p>
              
              <div className="space-y-1">
                {Object.entries(scale.scales).map(([response, value]) => (
                  <div key={response} className="flex justify-between items-center text-xs">
                    <span className="text-gray-500 font-medium">{value}</span>
                    <span className="text-right">{response}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          
          {/* المقاييس المخصصة */}
          {Object.entries(customScales).map(([key, scale]) => (
            <div
              key={key}
              className={`
                border-2 rounded-lg p-4 cursor-pointer transition-all
                ${selectedScale === key 
                  ? 'border-purple-500 bg-purple-50' 
                  : 'border-gray-200 hover:border-gray-300'}
              `}
              onClick={() => {
                setSelectedScale(key);
                setScaleMapping(scale.values);
                setError(null);
              }}
            >
              <div className="flex items-center gap-3 mb-3">
                <Palette className={`w-6 h-6 ${selectedScale === key ? 'text-purple-500' : 'text-gray-500'}`} />
                <h4 className="font-semibold text-right flex-1 text-sm">{scale.name}</h4>
                {selectedScale === key && <CheckCircle2 className="w-5 h-5 text-purple-500" />}
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">مخصص</span>
              </div>
              
              <div className="space-y-1">
                {Object.entries(scale.values)
                  .sort(([, a], [, b]) => b - a)
                  .map(([response, value]) => (
                    <div key={response} className="flex justify-between items-center text-xs">
                      <span className="text-gray-500 font-medium">{value}</span>
                      <span className="text-right">{response}</span>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>

        {/* زر لإظهار المقياس المخصص */}
        <div className="text-center">
          <button
            onClick={() => setShowCustom(!showCustom)}
            className="text-blue-500 hover:text-blue-600 underline flex items-center gap-2 mx-auto"
          >
            <Plus className="w-4 h-4" />
            <span>{showCustom ? 'إخفاء المقياس المخصص' : 'أضف مقياس مخصص'}</span>
          </button>
        </div>
      </div>

      {/* عرض المقاييس المتعددة */}
      {multiScaleMode && Object.keys(columnScaleMappings).length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <BarChart3 className="w-6 h-6 text-green-600" />
            <h3 className="text-lg font-semibold text-right">المقاييس المطبقة حسب العمود</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(columnScaleMappings).map(([column, mapping]) => (
              <div key={column} className="border rounded-lg p-4">
                <h4 className="font-medium text-right mb-3 text-blue-800">{column}</h4>
                <div className="space-y-2">
                  {Object.entries(mapping)
                    .sort(([,a], [,b]) => b - a)
                    .map(([response, value]) => (
                    <div key={response} className="flex justify-between items-center text-sm bg-gray-50 p-2 rounded">
                      <span className="text-gray-600 font-medium">{value}</span>
                      <span className="text-right">{response}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* المقياس المخصص */}
      {showCustom && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4 text-right">إضافة مقياس مخصص</h3>
          <div className="flex gap-4 mb-4">
            <button
              onClick={handleAddCustomMapping}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 flex items-center gap-2"
            >
              <Plus size={20} />
              <span>إضافة</span>
            </button>
            <input
              type="number"
              value={newValue}
              onChange={(e) => setNewValue(Number(e.target.value))}
              min={0}
              max={5}
              step={0.1}
              className="w-24 p-2 border rounded-lg"
              placeholder="الدرجة"
            />
            <input
              type="text"
              value={newResponse}
              onChange={(e) => setNewResponse(e.target.value)}
              placeholder="نص الاستجابة"
              className="flex-1 p-2 border rounded-lg text-right"
              dir="rtl"
            />
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          )}
        </div>
      )}

      {/* المقياس الحالي */}
      {isScaleComplete && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="w-6 h-6 text-green-500" />
            <h3 className="text-lg font-semibold text-right">المقياس المطبق حالياً</h3>
          </div>
          
          <div className="space-y-3">
            {Object.entries(scaleMapping)
              .sort(([,a], [,b]) => b - a) // ترتيب حسب القيمة تنازلياً
              .map(([response, value]) => (
              <div key={response} className="flex items-center gap-4 bg-gray-50 p-3 rounded-lg">
                <button
                  onClick={() => handleDeleteMapping(response)}
                  className="text-red-500 hover:text-red-700 px-2"
                  title="حذف"
                >
                  ×
                </button>
                <input
                  type="number"
                  value={value}
                  onChange={(e) => handleUpdateValue(response, Number(e.target.value))}
                  min={0}
                  max={5}
                  step={0.1}
                  className="w-24 p-2 border rounded-lg"
                />
                <span className="flex-1 text-right font-medium">{response}</span>
              </div>
            ))}
          </div>

          <div className="mt-4 p-3 bg-green-50 rounded-lg">
            <p className="text-green-800 text-right text-sm">
              ✅ تم إعداد المقياس بنجاح! ({Object.keys(scaleMapping).length} استجابة)
            </p>
          </div>
        </div>
      )}

      {/* أزرار التنقل */}
      <div className="flex justify-between items-center">
        <button
          onClick={() => setCurrentPage('axis_management')}
          className="px-6 py-2 rounded-lg text-gray-600 hover:bg-gray-100 border border-gray-300"
        >
          العودة إلى إدارة المحاور
        </button>

        <button
          onClick={handleProceed}
          disabled={!canProceedToAnalysis()}
          className={`
            px-8 py-3 rounded-lg flex items-center gap-2 font-semibold text-lg
            ${canProceedToAnalysis()
              ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 shadow-lg'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'}
          `}
        >
          {canProceedToAnalysis() && <CheckCircle2 size={20} />}
          <span>المتابعة إلى تحليل البيانات</span>
          {!canProceedToAnalysis() && <AlertCircle size={16} />}
        </button>
      </div>

      {/* رسالة الحالة */}
      {!canProceedToAnalysis() && (
        <div className="mt-4 p-4 bg-yellow-50 rounded-lg text-right">
          <p className="text-yellow-800 mb-2 font-medium">
            لا يمكن المتابعة إلى التحليل. تأكد من:
          </p>
          <ul className="text-yellow-700 text-sm space-y-1">
            <li>• رفع ملف البيانات واختيار عمود الورش</li>
            <li>• إنشاء محور واحد على الأقل مع أسئلة</li>
            <li>• إعداد مقياس الاستجابة</li>
          </ul>
        </div>
      )}
    </div>
  );
}